import BaseHelper from './BaseHelper'
import { select as d3_select } from 'd3-selection'

function getDistance(a, b) {
  const x = Math.abs(a[0] - b[0])
  const y = Math.abs(a[1] - b[1])
  return Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2))
}

function magneticSelect(e) {
  const [, { parentId }] = getCloser(e, this.items.merge(this.itemsEnter))
  const line = this.data.find(d => d.id === parentId)
  this._setSelection(e, line)
}

function resetHighlight(e) {
  if (!this.highlighted.length) return
  if (this.$root.contains(e.target) || this.$root === e.target) return
  this.highlighted = []
  this._updateHighlighted()
  this.$root.dispatchEvent(new CustomEvent('update:highlight', { detail: this.highlighted }))
}

function getCloser(e, items) {
  const { clientX, clientY } = e
  let closerItem
  items.nodes().reduce((min, item) => {
    const bbox = item.getBoundingClientRect()
    const distance = getDistance([clientX, clientY], [bbox.x, bbox.y])
    if (distance < min) {
      closerItem = item
      return distance
    }

    return min
  }, Infinity)

  return [closerItem, d3_select(closerItem).datum()]
}

const magneticHighlight = (function() {
  let waiting = false
  let pending = false

  function _magneticHighlight(e) {
    const [el, { parentId }] = getCloser(e, this.items.merge(this.itemsEnter))
    el.dispatchEvent(new MouseEvent('mousemove'))
    if (this.highlighted.map(d => d.id).indexOf(parentId) >= 0) return
    this.highlighted = [this.data.find(d => d.id === parentId)].concat(this.selection)
    this._updateHighlighted()
    this.$root.dispatchEvent(new CustomEvent('update:highlight', { detail: this.highlighted }))
  }

  let event
  return function(e) {
    event = e
    if (!waiting) {
      _magneticHighlight.call(this, e)
      waiting = true
      pending = false
      setTimeout(() => {
        waiting = false
        if (pending) {
          _magneticHighlight.call(this, event)
          pending = false
        }
      }, 200)
    } else {
      pending = true
    }
  }
})()

export default class SelectableHelper extends BaseHelper {
  static configName = 'SelectableHelper'

  static defaultConfig = {
    index: 103,
    /**
     * Enable/disable selection of the items. When enabled, items will be highlighted on click/tap.
     */
    enableSelection: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    enableHighlight: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    magnetic: {
      type: Boolean,
      default: false,
      exposed: true,
    },
  }
  static init() {
    this.selection = []
    this.highlighted = []

    const self = this
    d3_select(this.$root).on('click', e => {
      if (this.config.SelectableHelper.magnetic) return
      if (self.constructor.name === 'ChoroplethChart') return
      if (e.currentTarget === e.target || d3_select(e.target).datum() === null) {
        self.selection = []
        self.highlighted = []
        this.$root.dispatchEvent(new CustomEvent('update:selection', { detail: this.selection }))
        self.$root.dispatchEvent(new MouseEvent('mouseover'))
        self._updateSelection()
      }
    })

    this._magneticSelect = magneticSelect.bind(this)
    this._magneticHighlight = magneticHighlight.bind(this)
    this._resetHighlight = resetHighlight.bind(this)
  }

  static afterDraw() {
    let enters = this.itemsEnter
    let self = this
    if (enters) {
      enters.on('click', (e, d) => {
        if (self.config.SelectableHelper.magnetic) return
        if (d.parentId) {
          if (self.data.lines) {
            d =
              self.data.lines.find(dd => dd.id === d.parentId) ||
              self.data.bars.find(dd => dd.id === d.parentId)
          } else {
            d = self.data.find(dd => dd.id === d.parentId)
          }
        }
        d = { ...d, id: String(d.id).replace(/--.*/, '') }
        self._setSelection(e, d)
        e.currentTarget.dispatchEvent(new MouseEvent('mousemove'))
        e.target.dispatchEvent(new MouseEvent('mousemove'))
      })

      if (this.config.SelectableHelper.enableHighlight) {
        enters.on('mouseover mouseout', (e, d) => {
          if (self.config.SelectableHelper.magnetic) return
          if (d.parentId) {
            if (self.data.lines) {
              d =
                self.data.lines.find(dd => dd.id === d.parentId) ||
                self.data.bars.find(dd => dd.id === d.parentId)
            } else {
              d = self.data.find(dd => dd.id === d.parentId)
            }
          }
          d = { ...d, id: String(d.id).replace(/--.*/, '') }
          self._setHighlighted(e, d)
        })

        if (this.config.SelectableHelper.magnetic) {
          this.$root.removeEventListener('click', this._magneticSelect)
          this.$root.addEventListener('click', this._magneticSelect)
          this.$root.removeEventListener('mousemove', this._magneticHighlight)
          this.$root.addEventListener('mousemove', this._magneticHighlight)
          document.body.removeEventListener('mousemove', this._resetHighlight)
          document.body.addEventListener('mousemove', this._resetHighlight)
        }
      }
    }

    // update para cuando cambiamos la prop (no podemos hacer siempre xk se estropean las transitions...)
    if (!this.config.SelectableHelper.enableSelection && this.selection.length) {
      this._updateSelection()
    }

    // this._waitUntilTransitions.call(this).then(_ => {
    //   this._updateSelection()
    // })
  }

  static _waitUntilTransitions = function() {
    // Que feo queda
    return Promise.all(
      this.__itemsNames.map(name => {
        const enterName = name + 'EnterTrans'
        if (Object.hasOwnProperty.call(this, enterName)) {
          return new Promise(res => {
            const length = this[enterName].nodes().length
            if (!length) res()
            else {
              this[enterName].on('end', (d, i) => {
                if (i === length - 1) res()
              })
            }
          })
        } else {
          return Promise.resolve()
        }
      })
    )
  }

  static _updateSelection = function() {
    // Bastante guarrero, pero efectivo!
    if (!this.config.SelectableHelper.enableSelection) this.selection = []

    let items =
      (this.parentItems && this.parentItems.merge(this.parentItemsEnter)) ||
      (this.items && this.items.merge(this.itemsEnter)) ||
      (this.linesItems && this.linesItems.merge(this.linesItemsEnter))

    if (!items) return

    let self = this
    if (this.selection.length) {
      items
        // Desactivem transition per evitar conflictes amb les enter/exit transitions
        // .transition()
        // .duration(200)
        .style('opacity', dd =>
          this.selection.map(d => d.id).indexOf(String(dd.id).replace(/--.*/, '')) >= 0 ? 1 : 0.4
        )

      if (this.setSelectedFunc) {
        items
          // Desactivem transition per evitar conflictes amb les enter/exit transitions
          // .transition()
          // .duration(200)
          .each(function(dd) {
            if (self.selection.map(d => d.id).indexOf(String(dd.id).replace(/--.*/, '')) >= 0)
              d3_select(this).call(self.setSelectedFunc.bind(self), dd)
            else d3_select(this).call(self.unsetSelectedFunc.bind(self), dd)
          })
      }
    } else {
      items
        // Desactivem transition per evitar conflictes amb les enter/exit transitions
        // .transition()
        // .duration(200)
        .style('opacity', 1)

      if (this.unsetSelectedFunc) {
        items
          // Desactivem transition per evitar conflictes amb les enter/exit transitions
          // .transition()
          // .duration(200)
          .each(function(dd) {
            d3_select(this).call(self.unsetSelectedFunc.bind(self), dd)
          })
      }
    }
  }

  static _updateHighlighted = function() {
    // Bastante guarrero, pero efectivo!
    if (!this.config.SelectableHelper.enableSelection) this.selection = []

    const highlighteds = this.highlighted.map(d => d.id)
    const items =
      (this.parentItems && this.parentItems.merge(this.parentItemsEnter)) ||
      (this.items && this.items.merge(this.itemsEnter)) ||
      (this.linesItems && this.linesItems.merge(this.linesItemsEnter))

    const self = this
    if (highlighteds.length) {
      items
        // Desactivem transition per evitar conflictes amb les enter/exit transitions
        // .transition()
        // .duration(200)
        .style('opacity', dd =>
          highlighteds.indexOf(String(dd.id).replace(/--.*/, '')) >= 0 ? 1 : 0.3
        )
        .selectAll('.break-point')
        .select('circle')
        .style('opacity', dd => (highlighteds.indexOf(dd.parentId) >= 0 ? 1 : 0))

      if (this.setHighlightedFunc) {
        items
          // Desactivem transition per evitar conflictes amb les enter/exit transitions
          // .transition()
          // .duration(200)
          .each(function(dd) {
            if (self.highlighted.map(d => d.id).indexOf(String(dd.id).replace(/--.*/, '')) >= 0)
              d3_select(this).call(self.setHighlightedFunc.bind(self), dd)
            else d3_select(this).call(self.unsetHighlightedFunc.bind(self), dd)
          })
      }
    } else {
      items
        // Desactivem transition per evitar conflictes amb les enter/exit transitions
        // .transition()
        // .duration(200)
        .style('opacity', 1)

      if (this.unsetHighlightedFunc) {
        items
          // Desactivem transition per evitar conflictes amb les enter/exit transitions
          // .transition()
          // .duration(200)
          .each(function(dd) {
            d3_select(this).call(self.unsetHighlightedFunc.bind(self), dd)
          })
      }
    }
  }
  static _setSelection = function(e, d) {
    // this.highlighted = []
    if (this.selection.map(d => d.id).indexOf(d.id) >= 0) {
      this.selection = this.selection.filter(dd => dd.id !== d.id)
      // else this.selection=[d] //this.selection.push(d)
    } else this.selection.push(d)

    this._updateSelection()
    this.$root.dispatchEvent(new CustomEvent('update:selection', { detail: this.selection }))
  }

  static _setHighlighted = function(e, d) {
    if (this.highlighted.map(d => d.id).indexOf(d.id) >= 0) {
      this.highlighted = this.highlighted.filter(dd => dd.id !== d.id).concat(this.selection)
      // else this.highlighted=[d] //this.highlighted.push(d)
    } else this.highlighted.push(d)

    this._updateHighlighted()
    this.$root.dispatchEvent(new CustomEvent('update:highlight', { detail: this.highlighted }))
  }
}
